import React from 'react';
import styled from 'styled-components';
import { BREAKPOINTS, PRIMARY_COLOR } from '../../core/constants';
import useTranslation from '../../hooks/useTranslation';
import { LocalesUnion } from '../../types/interfaces';
import { APPLE_STORE_URL, PLAY_STORE_URL } from '../../utils/constants';

interface CProps {
  locale: LocalesUnion;
}

const RightHeader = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  position: relative;
`;

const AbsolutePlaceholder = styled.div`
  position: absolute;
  top: -100px;

  @media ${BREAKPOINTS.md} {
    top: 5px;
  }
`;

const AppleBadge = styled.img`
  width: 140px;
  height: 41px;
  margin-left: 2px;
  margin-right: 10px;
`;

const PlayBadge = styled.img`
  width: 158px;
  height: 61px;
  margin-top: -10px;
  margin-left: -10px;
`;

const IntroContent = styled.div`
  margin-left: 40px;

  @media ${BREAKPOINTS.md} {
    margin-left: 0px;
  }
`;

const LeftHeader = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  margin-top: 100px;
  justify-content: flex-end;
  align-items: center;

  @media ${BREAKPOINTS.md} {
    justify-content: center;
    text-align: center;
  }

  @media ${BREAKPOINTS.lg} {
    justify-content: flex-end;
    padding-bottom: 50px;
    margin-top: 50px;
  }

  @media ${BREAKPOINTS.xl} {
    margin-top: 50px;
  }
`;

const ImagePlaceholder = styled.img`
  min-height: 600px;
  min-width: 400px;
  max-height: 10vh;
  object-fit: contain;
`;

const Title = styled.span`
  font-size: 90px;
  font-weight: bold;
  margin-left: 3px;
  color: white;

  @media ${BREAKPOINTS.xl} {
    font-size: 70px;
  }

  @media ${BREAKPOINTS.lg} {
    margin-top: 5px;
    font-size: 50px;
  }
`;

const Subtitle = styled.h1`
  margin-top: -5px;
  margin-left: 5px;
  font-size: 40px;
  font-weight: normal;
  max-width: 600px;
  color: white;

  @media ${BREAKPOINTS.xl} {
    margin-top: 5px;
    font-size: 30px;
    max-width: 500px;
  }

  @media ${BREAKPOINTS.lg} {
    margin-top: 5px;
    font-size: 26px;
  }

  @media ${BREAKPOINTS.md} {
    margin-top: 5px;
    font-size: 26px;
  }
`;

const HeaderContent = styled.div`
  position: relative;
  height: 40vh;
  min-height: 400px;
  background: ${PRIMARY_COLOR};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;

  @media ${BREAKPOINTS.md} {
    flex-direction: column;
  }
`;

const Header = styled.div`
  margin: 0px;
  width: 100%;
  min-height: 950px;
  overflow: hidden;

  @media ${BREAKPOINTS.md} {
    overflow: visible;
  }
`;

const BubbleOne = styled.svg`
  position: absolute;
  overflow: hidden;
  pointer-events: none;

  @media ${BREAKPOINTS.sm} {
    top: 80px;
  }
`;

const Intro: React.FC<CProps> = ({ locale }) => {
  const { t } = useTranslation(locale);

  return (
    <div>
      <Header>
        <HeaderContent>
          <BubbleOne viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <path
              fill="rgba(255,255,255,0.1)"
              d="M58.9,-19.2C64.1,-3.3,47.5,19.6,25.3,36.1C3,52.5,-24.8,62.5,-45.8,50.3C-66.8,38.1,-80.9,3.6,-72.1,-17.2C-63.3,-38.1,-31.6,-45.3,-2.4,-44.6C26.9,-43.8,53.8,-35,58.9,-19.2Z"
              transform="translate(100 100)"
            />
          </BubbleOne>
          <svg
            style={{ position: 'absolute', pointerEvents: 'none' }}
            viewBox="0 0 200 200"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill="rgba(255,255,255,0.08)"
              d="M70.5,-23.3C79.1,3.5,65.3,37.2,42.5,52.2C19.7,67.3,-12,63.7,-30.3,48.8C-48.6,33.8,-53.5,7.4,-46.2,-17.6C-38.9,-42.6,-19.5,-66.2,5.8,-68.1C31,-70,62,-50.1,70.5,-23.3Z"
              transform="translate(-20 100)"
            />
          </svg>
          <LeftHeader>
            <IntroContent>
              <Title>Deevent</Title>
              <Subtitle>{t('HOMEPAGE_SUBTITLE')}</Subtitle>
              <div className="d-flex mt-4 justify-content-center justify-content-md-start">
                <a
                  href="https://apps.apple.com/us/app/deevent/id1519499216?ign-mpt=uo%3D2"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <AppleBadge
                    width={158}
                    height={41}
                    alt="Apple Store icon"
                    src={APPLE_STORE_URL}
                  />
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=app.deevent"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <PlayBadge
                    alt="Google Play Store icon"
                    src={PLAY_STORE_URL}
                    width={158}
                    height={61}
                  />
                </a>
              </div>
            </IntroContent>
          </LeftHeader>
          <RightHeader>
            <AbsolutePlaceholder>
              <ImagePlaceholder
                width="300"
                height="700"
                src="/images/screenshot.png"
                alt="placeholder"
              />
            </AbsolutePlaceholder>
          </RightHeader>
        </HeaderContent>
        <svg
          style={{ marginTop: -1 }}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1440 320"
        >
          <path
            fill={PRIMARY_COLOR}
            fillOpacity="0.6"
            d="M0,288L80,288C160,288,320,288,480,240C640,192,800,96,960,74.7C1120,53,1280,107,1360,133.3L1440,160L1440,0L1360,0C1280,0,1120,0,960,0C800,0,640,0,480,0C320,0,160,0,80,0L0,0Z"
          />
          <path
            fill={PRIMARY_COLOR}
            fillOpacity="1"
            d="M0,274L0,276C170,278,310,288,480,240C640,192,800,96,960,74.7C1120,33,1270,100,1460,133.3L1440,150L1440,0L1360,0C1280,0,1120,0,960,0C800,0,640,0,480,0C320,0,160,0,80,0L0,0Z"
          />
        </svg>
      </Header>
    </div>
  );
};

export default Intro;
